import React, { useMemo } from 'react';
import { Select, Form, Typography, Col, Alert, Space } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { FieldConfig } from '../../types/fieldTypes';
import { useValidator } from '../../hooks/useValidator';
import { useApiOptions } from '../../hooks/useApiOptions';

const { Text } = Typography;

interface MultiSelectFieldProps {
  field: FieldConfig;
  value: any;
  onChange: (e: any) => void;
  mode: 'edit' | 'view';
  formData?: Record<string, any>;
}

export const MultiSelectField: React.FC<MultiSelectFieldProps> = ({
  field,
  value,
  onChange,
  mode,
  formData = {},
}) => {
  const { t } = useTranslation();
  const { touched, validationError, handleBlur, handleValidate } = useValidator(
    { field, value, formData }
  );

  // Fetch API options if API config is provided
  const {
    options: apiOptions,
    loading: apiLoading,
    error: apiError,
  } = useApiOptions({ apiConfig: field.api, formData });

  // Combine static options and API options
  const allOptions = useMemo(() => {
    if (field.api) {
      return apiOptions; // Use API options if API is configured
    }
    return field.options || []; // Use static options otherwise
  }, [field.api, field.options, apiOptions]);

  const handleChange = (selectedValues: string[]) => {
    const syntheticEvent = {
      target: {
        name: field.name,
        value: selectedValues,
      },
    };
    onChange(syntheticEvent);
    handleValidate(selectedValues);

    // Call custom callbacks if they exist
    if (field.customProps?.onFieldChange) {
      field.customProps.onFieldChange(selectedValues, field.name, formData);
    }

    if (field.customProps?.onFormChange) {
      const updatedFormData = { ...formData, [field.name]: selectedValues };
      field.customProps.onFormChange(updatedFormData, field.name);
    }
  };

  if (mode === 'view') {
    return (
      <Col span={field.layout?.span || 24}>
        <Form.Item label={<Text type="secondary">{field.label}</Text>}>
          <Select
            mode="multiple"
            value={Array.isArray(value) ? value : []}
            disabled
            style={{ width: '100%' }}
            placeholder={t('multi_select_field.none_selected')}
          >
            {allOptions.map(option => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      </Col>
    );
  }

  const colProps = {
    span: field.layout?.span || 24,
    offset: field.layout?.offset || 0,
    order: field.layout?.order,
  };

  return (
    <Col {...colProps} className={field.layout?.className}>
      <Form.Item
        label={
          <Space>
            {field.label}
            {apiLoading && <Text type="secondary">{t('multi_select_field.loading_suffix')}</Text>}
          </Space>
        }
        required={field.required}
        validateStatus={validationError && touched ? 'error' : ''}
        help={
          <Space direction="vertical" size="small">
            {field.customProps?.description && (
              <Text type="secondary">{field.customProps.description}</Text>
            )}
            {apiError && (
              <Alert
                message={apiError}
                type="error"
                icon={<ExclamationCircleOutlined />}
                showIcon
              />
            )}
            {validationError && touched && (
              <Text type="danger">{validationError}</Text>
            )}
          </Space>
        }
      >
        <Select
          mode="multiple"
          placeholder={
            apiLoading
              ? t('dynamic_form.loading_options')
              : field.customProps?.placeholder || t('dynamic_form.select_multiple')
          }
          value={Array.isArray(value) ? value : []}
          onChange={handleChange}
          onBlur={handleBlur}
          loading={apiLoading}
          disabled={field.customProps?.disabled || apiLoading}
          size="large"
          style={{ width: '100%' }}
          status={validationError && touched ? 'error' : ''}
          {...(field.customProps &&
            Object.fromEntries(
              Object.entries(field.customProps).filter(
                ([key]) =>
                  ![
                    'onFieldChange',
                    'onFormChange',
                    'description',
                    'disabled',
                    'placeholder',
                  ].includes(key)
              )
            ))}
        >
          {allOptions.map(option => (
            <Select.Option key={option.value} value={option.value}>
              {option.label}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
    </Col>
  );
};
